import { useState, useEffect, useRef } from 'react';
import { api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Badge } from './ui/badge';
import { Search, Package, ArrowUp, ArrowDown, PlusCircle, AlertTriangle, WifiOff } from 'lucide-react';
import { searchProductsLocal, mergeLiveResults } from '../lib/productSearchLocal';

// How long a search may wait on the server. Without a limit, a dead uplink
// (wifi up, internet down) left the cashier staring at an empty box for the
// browser's own give-up time — 30–60 s (owner report 2026-09-21).
const LIVE_REFRESH_TIMEOUT_MS = 4000;   // cacheFirst: nobody is waiting on this one
const SERVER_FIRST_TIMEOUT_MS = 5000;   // server-first pages: then the saved list answers

export default function SmartProductSearch({
  onSelect,
  branchId,
  onCreateNew,
  // ── Optional: 'request' mode for Branch Stock Requests ───────────────────
  // Renders a compact row showing supply-branch stock + your-branch stock
  // (no prices/cost/reserved). Same keyboard nav, dropdown positioning,
  // scrollIntoView, and offline fallback as the default 'sales' mode.
  mode = 'sales',
  alsoBranchId = '',
  placeholder,
  // ── Optional: custom search endpoint (for Branch Pickup serving-branch search)
  // When set, uses this path instead of /products/search-detail.
  // Extra params (e.g. serving_branch_id) are passed via extraParams.
  searchEndpoint = null,
  extraParams = {},
  // ── Optional: hide repack children (audit Group B C3) ────────────────────
  // POs must purchase the parent (sack) product — the backend rejects repack
  // children with a 400, so the picker hides them up front.
  excludeRepacks = false,
  // ── Optional: answer from the device's saved catalog FIRST (sales screens) ─
  // Results appear at once, online or offline — exactly like Quick mode — and
  // the server's live stock / reserved / incoming / prices are merged into the
  // rows afterwards. Pages that need ANOTHER branch's stock (requests, pickup,
  // transfers) or the newest catalog (POs) keep asking the server first.
  cacheFirst = false,
  // The page already knows the server is unreachable → don't even try it.
  offline = false,
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [noResults, setNoResults] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [fuzzyHint, setFuzzyHint] = useState(null);
  // null | 'offline' (no server) | 'slow' (server did not answer in time)
  const [offlineFallback, setOfflineFallback] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0, maxHeight: 400, flipUp: false });
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  // Every search gets a number; an answer is only used if its number is still
  // the latest. Without it a slow reply for "gali" could land on top of the
  // results for "galimax 1".
  const searchSeqRef = useRef(0);
  const abortRef = useRef(null);

  // Position the dropdown using fixed coords to escape any overflow:hidden/auto ancestor.
  // Auto-flip: when there's < 320px below the input AND more space above, render
  // ABOVE the input instead. Keeps the result list visible whether the cashier
  // is typing on a row near the bottom of the screen or near the top.
  // Also clamps max-height to the available space so the list never spills
  // past the viewport edge.
  const updateDropdownPos = () => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    const spaceBelow = vh - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    // Flip up when there isn't comfortable room below AND we have more headroom up
    const flipUp = spaceBelow < 280 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(180, Math.min(400, flipUp ? spaceAbove : spaceBelow));
    setDropdownPos({
      top: flipUp ? null : rect.bottom + 4,
      bottom: flipUp ? vh - rect.top + 4 : null,
      left: rect.left,
      width: Math.max(rect.width, 320),
      maxHeight,
      flipUp,
    });
  };

  useEffect(() => {
    if (!open) return;
    updateDropdownPos();
    window.addEventListener('scroll', updateDropdownPos, true);
    window.addEventListener('resize', updateDropdownPos);
    return () => {
      window.removeEventListener('scroll', updateDropdownPos, true);
      window.removeEventListener('resize', updateDropdownPos);
    };
  }, [open]);

  useEffect(() => {
    if (!query || query.length < 1) {
      searchSeqRef.current += 1;
      abortRef.current?.abort();
      setResults([]); setOpen(false); setNoResults(false); setFuzzyHint(null); setOfflineFallback(null);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const seq = ++searchSeqRef.current;
      abortRef.current?.abort();
      const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      abortRef.current = ctrl;
      const isCurrent = () => seq === searchSeqRef.current;
      const keep = (rows) => (excludeRepacks ? (rows || []).filter(p => !p.is_repack) : (rows || []));
      const serverHint = (rows) => (rows.length > 0 && rows[0]._fuzzy_hint ? rows[0]._fuzzy_hint : null);
      const browserOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
      const serverUnreachable = offline || browserOffline;

      const askServer = (timeout) => {
        const params = { q: query, branch_id: branchId, ...extraParams };
        if (alsoBranchId) params.also_branch_id = alsoBranchId;
        if (mode === 'request') params.limit = 8;
        return api.get(searchEndpoint || '/products/search-detail', { params, timeout, signal: ctrl?.signal });
      };
      const show = (rows, { hint, note }) => {
        setResults(rows);
        setFuzzyHint(hint);
        setOfflineFallback(note);
        setNoResults(rows.length === 0 && query.length >= 2);
        updateDropdownPos();
        setOpen(true);
        setActiveIndex(rows.length > 0 ? 0 : -1);
      };
      const searchSaved = async () => {
        try {
          const local = await searchProductsLocal(query, branchId);
          return { rows: keep(local.results), hint: local.fuzzyHint };
        } catch {
          return { rows: [], hint: null };
        }
      };

      if (cacheFirst) {
        // 1) The saved catalog answers at once.
        const saved = await searchSaved();
        if (!isCurrent()) return;
        show(saved.rows, { hint: saved.hint, note: serverUnreachable ? 'offline' : null });
        if (serverUnreachable) return;
        // 2) Live numbers follow. Rows never move under the cashier's cursor:
        //    a live row replaces its saved twin in place, new products append.
        try {
          const res = await askServer(LIVE_REFRESH_TIMEOUT_MS);
          if (!isCurrent()) return;
          const live = keep(res.data || []);
          if (saved.rows.length === 0) {
            show(live, { hint: serverHint(live), note: null });
          } else {
            setResults(prev => mergeLiveResults(prev, live));
            setOfflineFallback(null);
          }
        } catch {
          if (isCurrent()) setOfflineFallback('slow');
        }
        return;
      }

      // Server first (pages that need live / other-branch data), but never
      // without a time limit; then the saved catalog answers.
      if (!serverUnreachable) {
        try {
          const res = await askServer(SERVER_FIRST_TIMEOUT_MS);
          if (!isCurrent()) return;
          const rows = keep(res.data || []);
          show(rows, { hint: serverHint(rows), note: null });
          return;
        } catch {
          if (!isCurrent()) return;
        }
      }
      const saved = await searchSaved();
      if (!isCurrent()) return;
      show(saved.rows, { hint: saved.hint, note: serverUnreachable ? 'offline' : 'slow' });
    }, cacheFirst ? 80 : 200);
    return () => clearTimeout(debounceRef.current);
  }, [query, branchId, alsoBranchId, mode, cacheFirst, offline]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving the page: a late answer must not touch an unmounted box.
  useEffect(() => () => { searchSeqRef.current += 1; abortRef.current?.abort(); }, []);

  const handleKeyDown = (e) => {
    if (!open || !results.length) {
      if (e.key === 'Escape') { setOpen(false); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      selectProduct(results[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const selectProduct = (product) => {
    if (product?.disabled_at_branch) {
      // Block selection — caller's onSelect won't be invoked.
      return;
    }
    onSelect(product);
    setQuery('');
    setResults([]);
    setOpen(false);
    setActiveIndex(-1);
    setFuzzyHint(null);
    inputRef.current?.focus();
  };

  // Keep the active row centered in the dropdown so similarly-named items
  // (e.g. "Galimax 1, 2, 3, 4, 5") never disappear behind the expanded
  // detail card. `block: 'center'` instead of 'nearest' guarantees the
  // highlight is always visible regardless of the active row's height.
  useEffect(() => {
    if (activeIndex >= 0 && dropdownRef.current) {
      const item = dropdownRef.current.children[activeIndex];
      item?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, [activeIndex]);

  return (
    <div className="relative" data-testid="smart-product-search">
      <div className="relative">
        <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          ref={inputRef}
          data-testid="product-search-input"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (results.length) setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder={placeholder || (mode === 'request' ? 'Search product to request...' : 'Type product name or scan barcode...')}
          className="w-full h-9 pl-8 pr-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1A4D2E]/30 focus:border-[#1A4D2E] bg-white"
          autoComplete="one-time-code" data-lpignore="true"
        />
      </div>

      {open && (results.length > 0 || noResults) && (
        <div
          style={{
            position: 'fixed',
            ...(dropdownPos.flipUp
              ? { bottom: dropdownPos.bottom }
              : { top: dropdownPos.top }),
            left: dropdownPos.left,
            width: dropdownPos.width,
            maxHeight: dropdownPos.maxHeight,
            zIndex: 9999,
          }}
          className="bg-white border border-slate-200 rounded-lg shadow-xl overflow-y-auto flex flex-col"
          data-testid="search-results-dropdown"
          data-flip={dropdownPos.flipUp ? 'up' : 'down'}
        >
          {/* Offline fallback chip — shows when results came from IndexedDB */}
          {offlineFallback && (
            <div
              data-testid="search-offline-chip"
              className="px-3 py-1.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2 text-[11px] text-amber-800 sticky top-0 z-10"
            >
              <WifiOff size={11} className="text-amber-600 shrink-0" />
              <span>
                {offlineFallback === 'slow'
                  ? 'Server is slow — showing saved products. Stock & prices may be a few minutes old.'
                  : 'Offline — searching cached products. Stock & prices may not reflect the latest changes.'}
              </span>
            </div>
          )}

          {/* Did-you-mean chip when fuzzy fallback fired */}
          {fuzzyHint && (
            <div
              data-testid="search-fuzzy-hint"
              className="px-3 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-2 text-xs text-amber-800 sticky top-0 z-10"
            >
              <AlertTriangle size={12} className="text-amber-600 shrink-0" />
              <span className="flex-1">
                No exact match for <strong>"{fuzzyHint.query}"</strong> — showing closest {fuzzyHint.count === 1 ? 'match' : 'matches'}.
              </span>
            </div>
          )}

          <div ref={dropdownRef}>
          {results.map((p, i) => {
            const isDisabled = !!p.disabled_at_branch;
            // ── 'request' mode: compact row with supply + your-branch stock ──
            if (mode === 'request') {
              return (
                <div
                  key={p.id}
                  data-testid={`search-result-${p.id}`}
                  onMouseDown={() => selectProduct(p)}
                  onMouseEnter={() => !isDisabled && setActiveIndex(i)}
                  className={`px-3 py-2 border-b border-slate-100 last:border-0 transition-colors ${
                    isDisabled ? 'opacity-50 bg-slate-50/60 cursor-not-allowed'
                    : (i === activeIndex ? 'bg-emerald-50 border-l-[3px] border-l-emerald-700 cursor-pointer' : 'hover:bg-slate-50 cursor-pointer')
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-medium text-sm leading-snug">{p.name}</span>
                    <span className="text-[10px] text-slate-400 shrink-0">{p.sku || ''}</span>
                  </div>
                  <div className="flex gap-3 mt-0.5 text-[11px]">
                    <span className={`font-medium ${(p.available ?? 0) > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                      Supply: {(p.available ?? 0).toFixed(1)} {p.unit || ''}
                    </span>
                    {p.also_branch_stock !== undefined && (
                      <span className={`font-medium ${p.also_branch_stock > 0 ? 'text-amber-600' : 'text-red-500'}`}>
                        You: {Number(p.also_branch_stock).toFixed(1)} {p.unit || ''}
                      </span>
                    )}
                    {p.category && <span className="text-slate-400">· {p.category}</span>}
                  </div>
                </div>
              );
            }
            // ── 'sales' mode (default): rich row with prices, capital, available, reserved ──
            return (
            <div
              key={p.id}
              data-testid={`search-result-${p.id}`}
              onMouseDown={() => selectProduct(p)}
              onMouseEnter={() => !isDisabled && setActiveIndex(i)}
              className={`px-3 py-2.5 border-b border-slate-100 last:border-0 transition-colors ${
                isDisabled ? 'opacity-50 bg-slate-50/60 cursor-not-allowed'
                : (i === activeIndex ? 'bg-emerald-50 border-l-[3px] border-l-emerald-700 cursor-pointer' : 'hover:bg-slate-50 cursor-pointer')
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-sm leading-snug">{p.name}</span>
                    {p.is_repack && <Badge className="text-[9px] bg-amber-100 text-amber-700 shrink-0">R</Badge>}
                    {isDisabled && (
                      <Badge className="text-[9px] bg-amber-100 text-amber-700 shrink-0 gap-1">
                        Disabled at branch
                      </Badge>
                    )}
                  </div>
                  <span className="text-[10px] text-slate-400">{p.sku}</span>
                </div>
                <span className="text-sm font-bold text-[#1A4D2E] shrink-0">{formatPHP(p.prices?.retail)}</span>
              </div>

              {i === activeIndex && (
                <div className="mt-2 p-2.5 rounded-md bg-slate-50 border border-slate-100 text-xs animate-fadeIn">
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <span className="text-slate-400 block">Retail</span>
                      <span className="font-bold text-[#1A4D2E]">{formatPHP(p.prices?.retail)}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block">Capital</span>
                      <span className="font-bold">{formatPHP(p.cost_price)}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block">Available</span>
                      <span className={`font-bold ${p.available <= 0 ? 'text-red-600' : ''}`}>{p.available?.toFixed(1)} {p.unit}</span>
                    </div>
                    <div>
                      <span className="text-slate-400 block">Reserved / Coming</span>
                      <span className="font-bold">{p.reserved} / {p.coming}</span>
                    </div>
                  </div>
                  {p.is_repack && p.parent_name && (
                    <div className="mt-2 pt-2 border-t border-slate-200 flex items-center gap-2">
                      <Package size={12} className="text-amber-500" />
                      <span className="text-slate-500">Parent: <b>{p.parent_name}</b></span>
                      <span className="text-slate-400">Stock: <b>{p.parent_stock?.toFixed(1)} {p.parent_unit}</b></span>
                    </div>
                  )}
                </div>
              )}

              {i !== activeIndex && (
                <div className="flex gap-3 mt-0.5 text-[11px] text-slate-400">
                  <span>Capital: {formatPHP(p.cost_price)}</span>
                  <span>Avail: {p.available?.toFixed(0)} {p.unit}</span>
                  {p.reserved > 0 && <span className="text-amber-500">Rsv: {p.reserved}</span>}
                  {p.is_repack && <span className="text-amber-600">Parent: {p.parent_stock?.toFixed(0)} {p.parent_unit}</span>}
                </div>
              )}
            </div>
            );
          })}
          </div>

          {noResults && query.length >= 2 && (
            <div className="px-3 py-3 border-t border-slate-100">
              <p className="text-sm text-slate-500 mb-2">No product found for "<b>{query}</b>"</p>
              {onCreateNew ? (
                <button
                  data-testid="create-product-from-search"
                  onMouseDown={() => { onCreateNew(query); setOpen(false); setQuery(''); }}
                  className="flex items-center gap-2 w-full px-3 py-2 rounded-md bg-[#1A4D2E]/5 hover:bg-[#1A4D2E]/10 text-[#1A4D2E] text-sm font-medium transition-colors"
                >
                  <PlusCircle size={16} /> Create "{query}" as new product
                </button>
              ) : (
                <p className="text-xs text-slate-400">Product does not exist in the system</p>
              )}
            </div>
          )}
          {results.length > 0 && (
            <div className="px-3 py-1.5 bg-slate-50 text-[10px] text-slate-400 flex items-center gap-3 border-t sticky bottom-0">
              <span><ArrowUp size={10} className="inline" /><ArrowDown size={10} className="inline" /> navigate</span>
              <span>Enter to select</span>
              <span>Esc to close</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
